<template>
  <section class="relative">
    <div
      v-for="section in sections"
      :key="section.key"
      id="og-what-youll-discover"
      class="mb-10 health-section px-6 py-8 lg:py-[4rem] lg:pl-0 lg:pr-[2.1428571429rem]"
      :ref="el => setSectionRef(section.key, el)"
    >
      <div class="mb-6 border-b border-gray-300 pb-[1.07rem] !text-ash-200">
        <div class="flex items-center space-x-2">
          <Heading :level="2" color="text-ash-200">{{ section.title }}</Heading>
        </div>
      </div>
      <div
        :class="{
          'relative after:absolute after:bottom-0 after:left-0 after:h-full after:w-full after:content-[] after:bg-[linear-gradient(to_top,_white_20%,_transparent_80%)] overflow-hidden is-collapsed':
            !expanded[section.key] && section.allowShowMore,
          'h-[17.857142857rem]': !expanded[section.key] && section.allowShowMore,
          'mb-[1rem]': expanded[section.key]
        }"
      >
        <div
          :id="`section-content-${section.key}`"
          class="prose max-w-none"
          v-html="section.content"
        />
      </div>
      <div class="flex justify-center showmore" :class="{ 'relative -top-[0.714rem]': !expanded, 'relative -top-0': expanded }">
        <Button
          v-if="section.allowShowMore === true"
          class="button is-large show-more-less text-ash-500"
          @click="toggle(section.key)"
          :aria-expanded="expanded[section.key]"
          aria-controls="content-section"
          variant="secondary"
          theme="LIGHT"
          size="sm"
          :id="`content-section-${section.key}`"
          :aria-label="expanded[section.key] ? `Show less about ${section.title}` : `Show more about ${section.title}`"
          :title="expanded[section.key] ? `Show less about ${section.title}` : `Show more about ${section.title}`"
        >
          {{ expanded[section.key] ? 'Show Less' : 'Show More' }}
        </Button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { reactive, nextTick } from 'vue'
  import type { ContentSection } from '~~/shared/types/common'
  import Heading from '../../atom/heading.vue'
  import Button from '../../atom/button.vue'

  defineProps<{
    sections: ContentSection[]
  }>()
  
  const expanded = reactive<Record<string, boolean>>({})
  const sectionRefs = reactive<Record<string, HTMLElement | null>>({})

  const setSectionRef = (
    key: string,
    el: Element | ComponentPublicInstance | null
  ) => {
    if (!el) return
    if (el instanceof HTMLElement) {
      sectionRefs[key] = el
    } else if ('$el' in el) {
      sectionRefs[key] = el.$el as HTMLElement
    }
  }

  const toggle = async (key: string) => {
    const wasExpanded = expanded[key]
    expanded[key] = !expanded[key]  
    if (wasExpanded) {
      await nextTick()
      if (sectionRefs[key] && 'scrollIntoView' in sectionRefs[key]) {
        sectionRefs[key]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      }
    }
  }
</script>